// =============================================================================
// ANNOUNCEMENT-BAR-HORIZON — ONE Horizon `Component` that owns BOTH vendored announcement engines.
// HORIZON PILOT (announcement-bar twin): the Motto announcement bar's two classic scripts reparented
// onto Horizon's `Component` base class (import { Component } from '@theme/component', resolved via
// the head importmap in snippets/horizon-importmap.liquid), loaded as native ESM through a
// section-scoped `<script type="module">` in sections/announcement-bar-horizon.liquid — NOT the
// webpack src/js bundle. Follows assets/gallery-carousel-horizon.js + assets/multicolumn-horizon.js.
//
// MAPPING DECISION (two vendored files → ONE element): the original couples them 1 section : 2
// scripts — assets/announcement.js (Swiper vertical rotator, loaded only when the slider is on) and
// assets/ann-bar.js (--ann-height IntersectionObserver measurer, loaded always). ann-bar.js has no
// life apart from this bar (it only ever measures `document.querySelector('.section-announcement')`),
// so a second custom element would be pure ceremony; both behaviours live on
// `<announcement-bar-horizon>`:
//   • connectedCallback    → ENGINE 1 (rotator): only when the root carries .announcement-bar--swiper
//                            (= the liquid's has_slider), wait for `window.Swiper`, mount on `this`
//                            with the field-by-field vendored config. ENGINE 2 (measurer): always —
//                            observe the section wrapper, publish --ann-height (ownership-guarded),
//                            re-evaluate on the same three editor events the vendored file listened to.
//   • disconnectedCallback → abort the document listeners, clear the readiness poll, destroy the
//                            Swiper instance, disconnect the observer, and remove our own
//                            --ann-height write. FULL teardown — the vendored files tore down NOTHING.
//
// DELIBERATE FIXES over the vendored engines (rendered behaviour identical, resources correct):
//   • ann-bar.js's destroy leak: its `let observer` was LOCAL to each initAnnBar() call, so the
//     `if (observer) observer.disconnect()` inside createObserver could never reach a PREVIOUS call's
//     observer — every shopify:section:load/unload/reorder for ANY section stacked one more live
//     IntersectionObserver for the page's lifetime. Here the observer is an instance field: every
//     re-run disconnects the old one FIRST, and disconnectedCallback tears down the last.
//   • announcement.js's discarded instance: `new Swiper(...)` was never stored or destroyed; editor
//     churn orphaned instances. Here the single instance is stored, double-init-guarded, and
//     destroyed (destroy BEFORE any class/DOM cleanup) on disconnect.
//
// INDEPENDENCE from the vendored scripts (which stay live for the FROZEN original) — they can never
// touch this twin, and this twin never re-emits them:
//   1. assets/announcement.js line 3 gates BOTH its boot paths (currentScript anchor + the
//      document-wide `shopify:section:load` listener) on
//      `section.classList.contains('section-announcement')` — this twin's schema class is
//      `announcement-bar-horizon-section`; classList.contains() is exact-token → hard return.
//   2. assets/ann-bar.js line 3 only selects `document.querySelector(".section-announcement")` —
//      exact class token, never this twin's wrapper.
//   Conversely, --ann-height is a PAGE-GLOBAL contract with an existing vendored writer, so this
//   Component only publishes it while NO original `.section-announcement` exists in the document
//   (i.e. the twin has REPLACED the original — the intended end state); while an original coexists
//   the vendored measurer keeps sole ownership and this Component stands down (never two writers).
//
// `type="module"` scripts DEFER and run AFTER Motto's classic head scripts. `swiper-bundle.min.js`
// is a classic `defer` head script (layout/theme.liquid), so `window.Swiper` is normally ready
// before this module runs; the readiness poll below is defence-in-depth (theme-editor re-injection,
// load-order drift).
//
// UNTRACKED new file (alongside component.js / utilities.js / the other *-horizon.js pilots) —
// re-verify it exists before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

// The vendored engines' exact-token hook (assets/announcement.js:3 guard + assets/ann-bar.js:3
// selector) vs this twin's schema-class token (sections/announcement-bar-horizon.liquid schema).
const VENDORED_SECTION_CLASS = 'section-announcement';
const TWIN_SECTION_CLASS = 'announcement-bar-horizon-section';

/**
 * The announcement-bar custom element. It IS the `.announcement-bar` root (and, when the slider is
 * on, the `.announcement-bar--swiper.swiper` container Swiper mounts on — same node the vendored
 * engine mounted). `this.refs.wrapper` is the `.announcement-bar__wrapper` holding the
 * `.swiper-slide` message blocks.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - the `.announcement-bar__wrapper` the message blocks render into.
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBarHorizon extends Component {
  connectedCallback() {
    super.connectedCallback(); // populates this.refs from [ref] descendants
    if (this.#controller) return; // double-connect guard (theme-editor churn)

    // ---- ENGINE 1: Swiper rotator (assets/announcement.js parity) ----------------------------
    // Vendored second gate parity: it queried `.announcement-bar--swiper` inside the section and
    // bailed when absent (announcement.js:5–7). On the twin that modifier sits on this root element
    // (the liquid only adds it when enable_slider AND blocks are present).
    if (this.classList.contains('announcement-bar--swiper')) {
      this.#whenSwiperReady(() => this.#initSlider());
    }

    // ---- ENGINE 2: --ann-height measurer (assets/ann-bar.js parity) --------------------------
    // The vendored file re-ran initAnnBar on these three document-wide editor events (ann-bar.js:
    // 32–34, no event.target filtering); mirror that, but abortable (full teardown on disconnect)
    // and leak-free (#initMeasurer disconnects the previous observer before creating a new one).
    // The re-runs also re-evaluate --ann-height OWNERSHIP: if the original bar is added/removed in
    // the editor, the twin stands down / takes over accordingly.
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener('shopify:section:load', this.#onSectionChange, { signal });
    document.addEventListener('shopify:section:unload', this.#onSectionChange, { signal });
    document.addEventListener('shopify:section:reorder', this.#onSectionChange, { signal });
    this.#initMeasurer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort(); // removes the three document-level listeners
    this.#controller = null;
    if (this.#swiperPoll) {
      clearInterval(this.#swiperPoll);
      this.#swiperPoll = null;
    }
    // Destroy the engine instance FIRST (deleteInstance + cleanStyles), before any other cleanup —
    // the resource-correct order the vendored scripts never had.
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this.#annObserver?.disconnect();
    this.#annObserver = null;
    if (this.#ownsAnnHeight) {
      // We were the page's --ann-height writer; leaving the stale inline value behind would freeze
      // header/drawer offsets at the last measurement. Removing it lets every consumer use its
      // fallback; on re-connect (editor reorder/reload) the new observer re-fires immediately and
      // re-publishes.
      document.documentElement.style.removeProperty('--ann-height');
      this.#ownsAnnHeight = false;
    }
  }

  /** @type {any} */ swiper = null;
  #controller = null;
  #swiperPoll = null;
  #annObserver = null;
  #ownsAnnHeight = false;

  #onSectionChange = () => this.#initMeasurer();

  /**
   * `window.Swiper` is the vendored classic global loaded via a `defer` head script — normally
   * present before this deferred module runs (the vendored announcement.js relied on that same defer
   * order with NO guard). Poll briefly (≈5s cap) as defence-in-depth, then give up silently (the
   * server-rendered messages stay visible either way). Only one poll runs at a time.
   */
  #whenSwiperReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }
    if (this.#swiperPoll) return;
    let tries = 0;
    this.#swiperPoll = setInterval(() => {
      if (window.Swiper) {
        clearInterval(this.#swiperPoll);
        this.#swiperPoll = null;
        callback();
      } else if (++tries > 100) {
        clearInterval(this.#swiperPoll);
        this.#swiperPoll = null;
      }
    }, 50);
  }

  // ENGINE 1 — field-by-field the vendored assets/announcement.js config, scoped to this instance.
  #initSlider() {
    if (!this.isConnected) return; // a stale readiness poll may fire late
    // Double-init guard: Swiper attaches its instance to the mount element as `el.swiper`, and the
    // twin's mount element IS this component — so this one check covers our stored instance AND
    // Swiper's own marker (they are the same property).
    if (this.swiper) return;

    // slideCount is DOM-derived, exactly like the vendored engine (announcement.js:8): message
    // blocks with blank text render nothing, so the RENDERED count can be lower than the merchant's
    // block count — autoplay gating follows the rendered count. A single slide with loop:true logs
    // Swiper's harmless not-enough-slides warning — parity with the original.
    const wrapper = this.refs.wrapper ?? this;
    const slideCount = wrapper.querySelectorAll('.swiper-slide').length;
    // Read-for-parity: the vendored engine reads data-rows-mobile / data-rows but NO liquid ever
    // emits them (the twin deliberately doesn't either — see the section file), so both default to 1.
    const rowsMobile = Number(this.dataset.rowsMobile) || 1;
    const rowsDesktop = Number(this.dataset.rows) || 1;

    this.swiper = new window.Swiper(this, {
      // Vendored falsy-coercion quirk KEPT for parity (announcement.js:13): a merchant speed of 0
      // emits data-speed="0" and `Number('0') || 800` → 800 — instant transitions are unreachable
      // in the original too. Same for delay (:23/:35, 0 → 2000).
      speed: Number(this.dataset.speed) || 800,
      direction: 'vertical',
      spaceBetween: 0,
      slidesPerView: rowsMobile,
      autoHeight: true,
      loop: true,
      autoplay:
        slideCount > rowsMobile
          ? {
              enabled: true,
              delay: Number(this.dataset.delay) || 2000,
              pauseOnMouseEnter: true,
              disableOnInteraction: false,
            }
          : { enabled: false },
      breakpoints: {
        // Motto's 765 kept VERBATIM (deliberately NOT the brand 640/1280 tokens) — it must stay in
        // lockstep with the section's inline `@media (min-width: 765px)` --announcement-height flip
        // or heights would desync from the slidesPerView switch.
        765: {
          slidesPerView: rowsDesktop,
          autoplay:
            slideCount > rowsDesktop
              ? {
                  enabled: true,
                  delay: Number(this.dataset.delay) || 2000,
                  pauseOnMouseEnter: true,
                  disableOnInteraction: false,
                }
              : { enabled: false },
        },
      },
    });
  }

  // ENGINE 2 — the vendored assets/ann-bar.js measurer, ported verbatim EXCEPT: instance-held
  // observer (leak fix), ownership guard (never a second writer beside the vendored one), and the
  // twin's own wrapper as the target.
  #initMeasurer() {
    // Leak fix: disconnect the previous observer BEFORE anything else — the vendored version's
    // `let observer` was local to each initAnnBar() call, so its own disconnect could never reach a
    // previous call's observer and every editor section event stacked one more live observer.
    this.#annObserver?.disconnect();
    this.#annObserver = null;
    if (!this.isConnected) return;

    // OWNERSHIP GUARD: while an ORIGINAL bar exists, vendored ann-bar.js measures it (the FIRST
    // `.section-announcement` in document order) and owns the global var — the twin stands down.
    // The moment no original exists (the twin has replaced it), the twin publishes the measurement
    // itself so every consumer (menu-drawer heights, sticky-header tops, megamenu inset,
    // account-dialog top) keeps working. Re-evaluated on every editor section event.
    if (document.querySelector(`.${VENDORED_SECTION_CLASS}`)) {
      this.#ownsAnnHeight = false; // the vendored measurer is (again) the writer of record
      return;
    }

    // Parity target: ann-bar.js measured the SECTION WRAPPER (the element the sticky-header rules
    // transform); ours is the wrapper carrying the twin's schema class. Same box as this element
    // (both are height: var(--announcement-height)), so the intersection decay is identical.
    const target = this.closest(`.${TWIN_SECTION_CLASS}`) ?? this;

    // Verbatim vendored observer: 1000 thresholds → near-continuous callbacks while scrolling;
    // --ann-height decays smoothly (height × intersectionRatio) as the bar scrolls off, hard 0px
    // when not intersecting.
    this.#annObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const annBarHeight = target.getBoundingClientRect().height || 0;
            document.documentElement.style.setProperty(
              '--ann-height',
              `${(annBarHeight * entry.intersectionRatio).toFixed(2)}px`
            );
          } else {
            document.documentElement.style.setProperty('--ann-height', '0px');
          }
        });
      },
      {
        threshold: Array.from({ length: 1000 }, (_, i) => i / 1000),
      }
    );
    this.#annObserver.observe(target);
    this.#ownsAnnHeight = true;
  }
}

if (!customElements.get('announcement-bar-horizon')) {
  customElements.define('announcement-bar-horizon', AnnouncementBarHorizon);
}
